import type { ComboboxItem, ComboboxLikeRenderOptionInput, SelectProps } from "@mantine/core"
import {
  Box,
  Fieldset,
  Group,
  SegmentedControl,
  type SegmentedControlItem,
  Select,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core"
import { createContext, type PropsWithChildren, use } from "react"
import { useTranslation } from "react-i18next"
import { TeamsSelect, UsersSelect } from "@/components/forms"
import { PEERS_METHODS_HELP } from "@/components/surveys/constants"
import { icons } from "@/components/utils"

const ModeContext = createContext<string>("all")

export function AudienceSelector({
  mode,
  onChangeMode,
  segments,
  error,
  label,
  children,
}: PropsWithChildren<{
  mode: string
  segments: SegmentedControlItem[]
  error?: React.ReactNode
  label?: React.ReactNode
  onChangeMode: (mode: string) => void
}>) {
  return (
    <Stack gap={8}>
      {label && (
        <Text size="sm" fw={500}>
          {label}
        </Text>
      )}
      <ModeContext.Provider value={mode}>
        <SegmentedControl value={mode} onChange={onChangeMode} data={segments} w={420} />
        {children}
      </ModeContext.Provider>
      {error && (
        <Text size="xs" c="red">
          {error}
        </Text>
      )}
    </Stack>
  )
}

export function AudienceSettings({ mode, children }: PropsWithChildren<{ mode: string }>) {
  const currentMode = use(ModeContext)
  if (currentMode !== mode) return null
  return <Fieldset w={420}>{children}</Fieldset>
}

export function AudienceManualSettings({ teamProps, userProps }) {
  const { t } = useTranslation()

  const team_ids = teamProps.value || []
  const user_ids = userProps.value || []

  return (
    <AudienceSettings mode="manual">
      {/* Teams section */}
      <Stack gap={4}>
        <Text size="sm" fw={500}>
          {t("Departments")} {!!team_ids.length && `(${team_ids.length})`}
        </Text>
        <TeamsSelect label="" searchable placeholder={t("Select departments")} {...teamProps} />
      </Stack>

      {/* Users section */}
      <Stack gap={4} mt={8}>
        <Text size="sm" fw={500}>
          {t("Employees")} {!!user_ids.length && `(${user_ids.length})`}
        </Text>
        <UsersSelect placeholder={t("Select users")} searchable {...userProps} />
      </Stack>
    </AudienceSettings>
  )
}

export function AudiencePeersAutoSettings(props: SelectProps) {
  const { t } = useTranslation()
  const help = t(PEERS_METHODS_HELP[props.value as keyof typeof PEERS_METHODS_HELP])
  return (
    <AudienceSettings mode="auto">
      <Select
        allowDeselect={false}
        label={t("Peers to evaluate")}
        data={[
          { value: "teammates", label: t("Own departments") },
          { value: "manager", label: t("Manager & reports") },
          { value: "self", label: t("Self review") },
        ]}
        renderOption={({ option: opt, checked }: ComboboxLikeRenderOptionInput<ComboboxItem>) => (
          <Box>
            <Group wrap="nowrap" gap={4}>
              {checked && (
                <ThemeIcon color="gray.6" variant="white" size="xs" bg="transparent">
                  <icons.Check stroke={3} />
                </ThemeIcon>
              )}
              <Text size="sm">{opt.label}</Text>
            </Group>
            <Text size="xs" c="dimmed">
              {t(PEERS_METHODS_HELP[opt.value as keyof typeof PEERS_METHODS_HELP])}
            </Text>
          </Box>
        )}
        {...props}
      ></Select>
      <Text size="xs" c="dimmed" mt={4}>
        {help}
      </Text>
    </AudienceSettings>
  )
}
